import {
  AlertCircle,
  CheckCircle2,
  CircleDot,
  RotateCcw,
  Send,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/cn';
import { useTimeline, type TimelineEntry, type TimelineEventType } from './use-timeline';

// Timeline tab per docs/ui/operator-ui-tds.md §4.2.
//
// Merged stream of bus events for the case URN — warm-tier history layered
// with live events delivered via the central applyEvent merge (NEX-149).
// Saga compensation events render with a distinguishing icon. Older entries
// could lazy-load on scroll; today's demo lists fit in one viewport, so we
// render the full array.

interface TimelineTabProps {
  caseUrn: string;
}

export function TimelineTab({ caseUrn }: TimelineTabProps) {
  const { t } = useTranslation('caseWork');
  const timeline = useTimeline(caseUrn);

  if (timeline.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-14 w-full max-w-3xl" />
        <Skeleton className="h-14 w-full max-w-3xl" />
        <Skeleton className="h-14 w-full max-w-3xl" />
      </div>
    );
  }

  if (timeline.isError) {
    return (
      <Card className="max-w-3xl">
        <CardContent className="p-6 text-sm text-destructive">
          {t('timeline.loadError')}
        </CardContent>
      </Card>
    );
  }

  const entries = timeline.data ?? [];
  if (entries.length === 0) {
    return <EmptyState title={t('timeline.empty')} />;
  }

  return (
    <ol className="relative max-w-3xl space-y-3 border-l-2 border-border pl-6" data-testid="timeline-list">
      {entries.map((entry) => (
        <TimelineRow key={entry.entryUrn} entry={entry} />
      ))}
    </ol>
  );
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  const { t } = useTranslation('caseWork');
  const Icon = iconFor(entry.type, entry.isCompensation);
  return (
    <li className="relative">
      <span
        className={cn(
          'absolute -left-[33px] flex h-6 w-6 items-center justify-center rounded-full border-2 bg-background',
          entry.isCompensation
            ? 'border-amber-600 text-amber-700 dark:text-amber-300'
            : 'border-primary text-primary',
        )}
        aria-hidden
      >
        <Icon className="h-3 w-3" />
      </span>
      <Card>
        <CardContent className="flex items-start justify-between gap-3 p-3 text-sm">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium">
                {t(`timeline.type.${entry.type}`, { defaultValue: humanizeType(entry.type) })}
              </span>
              {entry.isCompensation ? (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs uppercase text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                  {t('timeline.compensation')}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-muted-foreground">{entry.summary}</p>
            {entry.details ? (
              <details className="mt-1 text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none">{t('timeline.details')}</summary>
                <p className="mt-1 whitespace-pre-wrap">{entry.details}</p>
              </details>
            ) : null}
            <p className="mt-1 truncate font-mono text-[10px] text-muted-foreground" title={entry.correlationUrn}>
              {t('shared.correlation', { urn: entry.correlationUrn })}
            </p>
          </div>
          <time className="shrink-0 text-xs text-muted-foreground" dateTime={entry.ts}>
            {new Date(entry.ts).toLocaleTimeString()}
          </time>
        </CardContent>
      </Card>
    </li>
  );
}

function iconFor(type: TimelineEventType, isCompensation: boolean): typeof CircleDot {
  if (isCompensation) return RotateCcw;
  switch (type) {
    case 'CaseOpened':
      return CircleDot;
    case 'BookingConfirmed':
    case 'CaseResolved':
    case 'DemandApproved':
    case 'CompensationCompleted':
      return CheckCircle2;
    case 'BookingFailed':
    case 'ReservationCancelled':
      return AlertCircle;
    case 'NotificationDispatched':
      return Send;
    default:
      return CircleDot;
  }
}

/** Last-resort label for an event type the bundles do not carry yet. */
function humanizeType(type: TimelineEventType): string {
  return type.replace(/([A-Z])/g, ' $1').trim();
}
